<template>
  <div class="error-page">
    <AntProvider :theme="theme" :configProviderProps="{ locale: locale }">
      <div class="error-page__content">
        <Resource
          :src="getFeUrl(isChinese() ? '/youyan-platform/png/no-data.png' : '/rendora/404.png')"
          :transform-resource="transformImage"
          :class="[
            'error-page__image',
            isChinese() ? 'error-page__image--chinese' : 'error-page__image--english',
          ]"
        />
        <div
          :class="[
            'error-page__title',
            isChinese() ? 'error-page__title--chinese' : 'error-page__title--english',
          ]"
        >
          {{ $t('dang-qian-ye-mian-bu-cun-zai') }}
        </div>
        <span v-if="!isChinese()" class="error-page__description">
          The page you are looking for might have been removed had its name changed or is
          temporarily unavailabel.
        </span>
        <a-button
          type="primary"
          :class="['error-page__button', !isChinese() && 'error-page__button--english']"
          @click="startToGoHome"
        >
          {{ $t('fan-hui-shou-ye') }}
        </a-button>

        <!-- 开发环境显示详细错误信息 -->
        <div v-if="isDev && props.error" class="error-page__error-details">
          <h3 class="error-page__error-details-title">🔍 开发环境错误详情:</h3>
          <div class="error-page__error-details-content">
            <div class="error-page__error-details-item">
              <strong>状态码:</strong> {{ props.error.statusCode }}
            </div>
            <div class="error-page__error-details-item">
              <strong>状态消息:</strong> {{ props.error.statusMessage }}
            </div>
            <div class="error-page__error-details-item">
              <strong>错误消息:</strong> {{ props.error.message }}
            </div>
            <div v-if="props.error.stack" class="error-page__error-details-stack">
              <strong>错误堆栈:</strong>
              <pre class="error-page__error-details-stack-content">{{ props.error.stack }}</pre>
            </div>
          </div>
        </div>
      </div>
    </AntProvider>
  </div>
</template>
<script lang="ts" setup>
import enUS from 'ant-design-vue/es/locale/en_US';
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import { transformImage } from '~/utils/';
import { isChinese } from '~/utils/area';
import { goHome } from '~/utils/permission';
import { getFeUrl } from '~/utils/url';
import { AntProvider } from './features/provider';

const locale = isChinese() ? zhCN : enUS;

const theme = 'light';

const props = defineProps({
  error: Object,
});

// 在开发环境下显示详细错误信息
const isDev = process.env.NODE_ENV === 'development';

onMounted(() => {
  // 记录错误信息到控制台
  if (props.error) {
    console.error('🚨 [ERROR PAGE] 错误页面显示:', {
      statusCode: props.error.statusCode,
      statusMessage: props.error.statusMessage,
      message: props.error.message,
      stack: props.error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    // 在开发环境下显示完整错误对象
    if (isDev) {
      console.group('🔍 [DEBUG] 完整错误信息');
      console.error('错误对象:', props.error);
      console.groupEnd();
    }
  }
});

function startToGoHome() {
  goHome();
}
</script>
<style lang="scss" scoped>
.error-page {
  padding-top: $top-height;
  height: calc(100vh - $top-height);

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    align-items: center;
    margin: 0 auto;
    text-align: center;
  }

  &__image {
    margin: 0 auto;

    &--chinese {
      width: 200px;
      height: 200px;
    }

    &--english {
      width: 944px;
      height: 321px;
    }
  }

  &__title {
    color: var(--text-font-80);
    font-weight: bold;

    &--chinese {
      font-size: 18px;
      margin: 30px 0;
    }

    &--english {
      font-size: 30px;
      margin-top: 15px;
      margin-bottom: 24px;
    }
  }

  &__description {
    display: block;
    width: 474px;
    color: #000;
    font-size: 17px;
    text-align: center;
    margin: 0 auto 32px;
  }

  &__button {
    &--english {
      width: 240px;
      height: 42px;
      border-radius: 15px;
    }
  }

  &__error-details {
    margin-top: 32px;
    padding: 16px;
    background-color: #f5f5f5;
    border-radius: 8px;
    text-align: left;
    max-width: 896px;
    margin-left: auto;
    margin-right: auto;

    &-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 16px;
      color: #dc2626;
    }

    &-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      font-size: 14px;
    }

    &-item {
      line-height: 1.5;
    }

    &-stack {
      margin-top: 16px;

      &-content {
        margin-top: 8px;
        padding: 8px;
        background-color: #e5e5e5;
        border-radius: 4px;
        font-size: 12px;
        overflow: auto;
        max-height: 160px;
      }
    }
  }
}
</style>
